
import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Plus, CreditCard, Trash2, Edit, MoreVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface CardsProps {
  onBack: () => void
}

export function Cards({ onBack }: CardsProps) {
  const [cards] = useState([])

  return (
    <div className="space-y-6 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Payment Cards</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your payment methods</p>
        </div>
      </div>

      {/* Add Card Button */}
      <Card className="glassmorphism p-4 border-white/20">
        <Button className="w-full gap-2 bg-primary/90 hover:bg-primary backdrop-blur-xl">
          <Plus className="h-5 w-5" />
          Add New Card
        </Button>
      </Card>

      {/* Cards List */}
      <div className="space-y-4">
        {cards.length === 0 ? (
          <Card className="glassmorphism p-8 text-center border-white/20">
            <div className="flex flex-col items-center gap-3">
              <div className="h-16 w-16 rounded-full bg-primary/10 backdrop-blur-xl flex items-center justify-center">
                <CreditCard className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="font-medium">No cards yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add your first payment card to get started
                </p>
              </div>
            </div>
          </Card>
        ) : (
          cards.map((card: any) => (
            <Card key={card.id} className="glassmorphism p-4 border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 backdrop-blur-xl flex items-center justify-center">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{card.name}</p>
                  <p className="text-sm text-muted-foreground">•••• {card.lastFour}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="backdrop-blur-xl">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glassmorphism border-white/20">
                    <DropdownMenuItem className="gap-2">
                      <Edit className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem className="gap-2 text-destructive">
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Info Card */}
      <Card className="glassmorphism p-4 border-white/20 bg-primary/5">
        <div className="flex gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 backdrop-blur-xl flex items-center justify-center flex-shrink-0">
            <CreditCard className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Manage Payment Methods</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add multiple cards to track expenses across different payment methods
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
